"use client";

import { getGtmUserData } from "@/helpers/getGtmUserData";
import { sendGTMEvent } from "@/helpers/sendGTMEvent";
import { usePathname, useRouter } from "@/i18n/navigation";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
  addItem,
  clearCart,
  decreaseQuantity,
  deleteItem,
  enableRenew,
  getTotalCartItems,
  getTotalCartPrice,
  increaseQuantity,
} from "@/redux/slices/cartSlice";
import { Package } from "@/types/packages/data-only/DataOnlyCountryPackages";
import { useAuth } from "@/providers/AuthProvider";
import { useCodiea } from "@codiea/tracking/react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export function useCartActions() {
  const [cartQuantity, setCartQuantity] = useState(1);
  const [showCart, setShowCart] = useState(false);
  const [isCheckoutPageLoading, setIsCheckoutPageLoading] = useState(false);

  const t2 = useTranslations(`Shared.productCart`);
  const { user } = useAuth();
  const { track } = useCodiea();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const pathname = usePathname();

  // Clear checkout spinner when leaving / returning from checkout (e.g. browser back)
  useEffect(() => {
    if (!pathname.includes("/checkout")) {
      setIsCheckoutPageLoading(false);
    }
  }, [pathname]);

  // Selectors
  const cartItems = useAppSelector((state) => state.cart);

  const totalCartQuantity = useAppSelector((state) =>
    getTotalCartItems(state.cart),
  );

  const totalCartPrice = useAppSelector((state) =>
    getTotalCartPrice(state.cart),
  );

  // Derived State (Centralized)
  const isCartHavePhonicoPackage = cartItems.some(
    (item) => item.provider === "Phonico",
  );

  const isCartHaveUltimatePackage = cartItems.some(
    (item) => item.provider === "ultimate_mobile",
  );

  // Add to Cart Logic with GTM + Codiea tracking
  function handleAddToCart({
    activePackages,
    selectedPackageId,
    countryFlag,
    goToCheckout,
  }: {
    activePackages: Package[];
    selectedPackageId: string;
    countryFlag: string;
    goToCheckout?: boolean;
  }) {
    if (!selectedPackageId) {
      toast.error("Please select a package first.");
      return;
    }

    if (totalCartQuantity >= 5) {
      toast.error(t2("cartFull"));
      setShowCart(true);
      return;
    }

    const searchedPackage = activePackages.find(
      (item) => item.id === selectedPackageId,
    );

    if (searchedPackage) {
      const { id, name, price, package_type } = searchedPackage;

      sendGTMEvent({
        event: "add_to_cart",
        user_data: getGtmUserData(user?.name),
        ecommerce: {
          currency: "USD",
          value: cartQuantity * price,
          items: [
            {
              item_id: id,
              item_name: name,
              price: price,
              quantity: cartQuantity,
              affiliation: "eSIMCard Web",
              item_brand: "eSIMCard",
              item_category: package_type,
            },
          ],
        },
      });

      void track({
        event: "add_to_cart",
        userId: user?.id != null ? String(user.id) : undefined,
        properties: {
          plan_id: String(id),
          value: cartQuantity * price,
          currency: "USD",
        },
      });

      const cartItem = {
        ...searchedPackage,
        quantity: cartQuantity,
        image_url: countryFlag,
        recurring: 0,
        can_renew: searchedPackage.can_renew ? true : false,
      };

      dispatch(addItem(cartItem));

      if (goToCheckout) {
        setIsCheckoutPageLoading(true);
        setCartQuantity(1);
        router.push("/checkout");
        return;
      }
      toast.success(t2("addToCartSuccess"));
      setShowCart(true);
      setCartQuantity(1);
    }
  }

  // Action Dispatches
  function handleDecreaseItemQuantity(itemId: string) {
    dispatch(decreaseQuantity(itemId));
  }

  function handleIncreaseItemQuantity(itemId: string) {
    if (totalCartQuantity >= 5) {
      toast.error(t2("cartFull"));
      return;
    }
    dispatch(increaseQuantity(itemId));
  }

  function handleDeleteItem(itemId: string) {
    dispatch(deleteItem(itemId));
  }

  function handleClearCart() {
    dispatch(clearCart());
  }

  function handleEnableRenew(itemId: string, isRenew: boolean) {
    dispatch(enableRenew({ id: itemId, isRenew }));
  }

  // Safe client-side quantity lookup avoiding nested selector rule violation
  function itemQuantityById(itemId: string) {
    const item = cartItems.find((item) => item.cart_id === itemId);
    return item ? item.quantity : 0;
  }

  return {
    // Local Hook States
    cartQuantity,
    setCartQuantity,
    showCart,
    setShowCart,
    isCheckoutPageLoading,
    setIsCheckoutPageLoading,

    // Selectors and Derived States
    cartItems,
    totalCartQuantity,
    totalCartPrice,
    isCartHavePhonicoPackage,
    isCartHaveUltimatePackage,

    // Cart Methods
    handleAddToCart,
    handleDecreaseItemQuantity,
    handleIncreaseItemQuantity,
    handleDeleteItem,
    handleClearCart,
    handleEnableRenew,
    itemQuantityById,
  };
}
